import { Button, Field } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AixQuill } from '../../../../AixQuill';
import { Input } from '../../../../Input';
import { LocalizedDatePicker } from '../../../../LocalizedDatePicker/LocalizedDatePicker';
import { NumberInput } from '../../../../NumberInput/NumberInput';
import { QUILL_DEFAULT_FORMATS, QUILL_DEFAULT_MODULES } from '../../../../Utils/quillDefaults';
import { SelectTeamsAssignmentWithResources } from './NewHomeworkDialog.interfaces';
import { parseMinutesStringToNumberIfNecessary } from './NewHomeworkDialog.utils';

export interface CommonHomeworkFormProps {
  selectedHomework?: SelectTeamsAssignmentWithResources;
  selectedHomeworkChanged?: (homework: SelectTeamsAssignmentWithResources) => void;
  /**
   * The currently selected course book date
   */
  minDate: Date;
}

export const CommonHomeworkForm: FC<CommonHomeworkFormProps> = (props) => {
  const { selectedHomework, selectedHomeworkChanged, minDate } = props;
  const { t } = useTranslation();
  const [quillText, setQuillText] = useState(selectedHomework?.assignmentDescription ?? '');

  useEffect(() => {
    if (selectedHomework) {
      selectedHomeworkChanged?.({
        ...selectedHomework,
        assignmentDescription: quillText,
      });
    }
  }, [quillText]);

  const handleDescriptionChanged = (description: string) => setQuillText(description);

  const handleNameChanged = (name: string) => {
    if (selectedHomework) {
      selectedHomeworkChanged?.({ ...selectedHomework, assignmentName: name });
    }
  };

  const handleMinutesChanged = (minutes: string) => {
    if (selectedHomework) {
      selectedHomeworkChanged?.({
        ...selectedHomework,
        assignmentMinutes: minutes,
      });
    }
  };

  const handleSelectedDateChanged = (date: Date) => {
    if (selectedHomework) {
      selectedHomeworkChanged?.({
        ...selectedHomework,
        assignmentDueDate: date.toISOString(),
      });
    }
  };

  const getValue = () => {
    const value = selectedHomework?.assignmentMinutes
      ? parseMinutesStringToNumberIfNecessary(selectedHomework.assignmentMinutes)
      : 0;
    return value;
  };

  return (
    <>
      <label className="inputLabel">
        {t('classbook:newassignment:dialog_assignment_title', {
          defaultValue: 'Name der Aufgabe',
        })}
      </label>
      <Input
        appearance="filled-darker"
        className="input w-full"
        value={selectedHomework?.assignmentName}
        onChange={(_, name) => {
          name ? handleNameChanged(name?.value) : null;
        }}
        contentAfter={
          (selectedHomework?.assignmentName?.length ?? 0) > 0 ? (
            <Button
              appearance="transparent"
              size="small"
              icon={<DismissRegular />}
              onClick={() => handleNameChanged('')}
              aria-label={t('classbook:newassignment:dialog_clear_assignment_title', {
                defaultValue: 'Aufgabenname löschen',
              })}
            />
          ) : undefined
        }
      />
      <div className="flex items-start gap-3 commonHomeworkFormInputRow">
        <Field label={t('classbook:newassignment:dialog_dueDate', { defaultValue: 'Fällig am' })}>
          <LocalizedDatePicker
            appearance="filled-darker"
            className="input"
            minDate={minDate}
            selectedDate={
              selectedHomework?.assignmentDueDate
                ? new Date(selectedHomework?.assignmentDueDate)
                : minDate
            }
            onDateChange={(_, data) => {
              data ? handleSelectedDateChanged?.(data?.value as Date) : null;
            }}
          />
        </Field>
        <NumberInput
          appearance="filled-darker"
          min={0}
          className="inputNumber"
          value={getValue()}
          max={60}
          label={t('classbook:newassignment:dialog_effort', {
            defaultValue: 'Geschätzer Aufwand (in Minuten)',
          })}
          onChange={(_, minutes) => {
            minutes ? handleMinutesChanged(minutes.value) : null;
          }}
        />
      </div>
      <span className="inputLabel">
        {t('classbook:newassignment:dialog_homework_desc', {
          defaultValue: 'Beschreibung',
        })}
      </span>
      <div>
        <AixQuill
          modules={QUILL_DEFAULT_MODULES}
          formats={QUILL_DEFAULT_FORMATS}
          className="aixQuill"
          onChange={handleDescriptionChanged}
          defaultValue={selectedHomework?.assignmentDescription ?? ''}
        />
      </div>
    </>
  );
};
